'use client';

import { useCallback } from 'react';
import type { CartSubselection } from '@/features/totems/domain/model/cart-item';
import type { MenuSnapshotProduct } from '@/features/totems/domain/model/menu-snapshot';
import { resolveLinePromo } from '@/features/totems/domain/service/menu-discount';
import { shouldOpenProductSheet } from '@/features/totems/domain/service/product-interaction';
import { getProductDisplayName } from '@/features/totems/domain/service/product-name';
import { getProductStockLimit } from '@/features/totems/domain/service/product-stock';
import { calculateCartItemTotal } from '@/features/totems/domain/service/totem-price';
import {
  useTotemActions,
  useTotemCartState,
  useTotemCatalog,
} from '@/features/totems/presentation/providers/web-totem/totem-provider';

export function useProductInteraction() {
  const { cart } = useTotemCartState();
  const { discountsByProduct } = useTotemCatalog();
  const { setSelectedProduct, triggerFlyToCart } = useTotemActions();

  const addProductToCart = useCallback(
    (product: MenuSnapshotProduct, quantity: number, subselections: CartSubselection[], fromRect?: DOMRect) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter(item => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity + quantity > stockLimit) {
        return;
      }

      // Solo las promos (method 'promo') se auto-aplican por línea; los cupones se tipean en el carrito.
      const fullPrice = Number(product.price);
      const promo = resolveLinePromo(fullPrice, discountsByProduct[product.id]);

      // Paridad con el servidor: `min_purchase` de la promo se evalúa contra el subtotal del
      // carrito una vez agregada la línea (a precio de lista).
      const lineAtFullPrice = calculateCartItemTotal({ unitPrice: fullPrice, quantity, subselections });
      const subtotalAfterAdd = cart.subtotal + lineAtFullPrice;
      const promoApplies = promo != null && (promo.minPurchase == null || subtotalAfterAdd >= promo.minPurchase);

      const effectiveUnitPrice = promoApplies && promo ? promo.finalPrice : fullPrice;
      const originalPrice = promoApplies && promo ? fullPrice : undefined;
      const promoPayload =
        promoApplies && promo
          ? {
              promoId: promo.id,
              discount: promo.amount,
              discountType: promo.type,
            }
          : undefined;

      cart.addItem({
        productId: product.id,
        name: getProductDisplayName(product),
        imageUrl: product.imageurl,
        unitPrice: effectiveUnitPrice,
        originalPrice,
        quantity,
        subselections,
        promo: promoPayload,
      });

      if (fromRect && product.imageurl) {
        triggerFlyToCart({
          imageUrl: product.imageurl,
          fromRect,
        });
      }
    },
    [cart, discountsByProduct, triggerFlyToCart]
  );

  const handleProductClick = useCallback(
    (product: MenuSnapshotProduct, fromRect: DOMRect) => {
      const stockLimit = getProductStockLimit(product.product_stock);
      const currentQuantity = cart.items
        .filter(item => item.productId === product.id)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (currentQuantity >= stockLimit) {
        return;
      }

      if (shouldOpenProductSheet(product, cart.items)) {
        setSelectedProduct(product);
        return;
      }

      addProductToCart(product, 1, [], fromRect);
    },
    [cart.items, setSelectedProduct, addProductToCart]
  );

  return {
    handleProductClick,
    addProductToCart,
  };
}
